import React, { useEffect, useRef } from "react";
import { ActionIcon, Autocomplete, Button, ColorInput, Container, DEFAULT_THEME, Divider, Group, Paper, ScrollArea, SimpleGrid, Space, Stack, Text, TextInput, Textarea, ThemeIcon, Title, useMantineTheme } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMediaQuery } from "@mantine/hooks";
import { IconCheck, IconChevronLeft, IconLock, IconPlus, IconTrash } from "@tabler/icons-react";

import { AzureADRoles } from "../../Auth/auth-roles";
import { RoleTemplate } from "../RoleTemplate";
import { useAccess } from "../../Auth/auth-functions";
import { useGetVendorTypesQuery } from "../../api/vendor.api";
import { MutableVendorDto, VendorContactDto, VendorDto } from "../../models/vendors.models";
import classes from "./VendorDetailView.module.css";

interface VendorEditViewProps {
	vendor?: VendorDto | null;
	onSave: (values: MutableVendorDto) => void;
	onClose: () => void;
	isSaving?: boolean;
	onChanged?: (changed: boolean) => void;
}

const emptyContact: VendorContactDto = {
	FullName: "",
	Role: "",
	Email: "",
	PhoneNumber: "",
	Colour: "#4dabf7",
};

export const VendorEditView: React.FC<VendorEditViewProps> = ({
	vendor,
	onSave,
	onClose,
	isSaving = false,
	onChanged,
}) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const canEditVendor = useAccess(AzureADRoles.Admin);
	const isNew = !vendor || vendor.VendorId === "new";
	const { data: vendorTypes } = useGetVendorTypesQuery();

	const form = useForm<MutableVendorDto>({
		initialValues: {
			VendorId: vendor?.VendorId || "",
			Name: vendor?.Name || "",
			Account: vendor?.Account || "",
			Colour: vendor?.Colour || "#4263eb",
			Description: vendor?.Description || "",
			Address: vendor?.Address || "",
			Phone: vendor?.Phone || "",
			Website: vendor?.Website || "",
			VendorType: vendor?.VendorType || "",
			Contacts: vendor?.Contacts?.length ? vendor.Contacts : [],
		},
		validate: {
			Name: (value) => (value.length < 3 ? "Name must be at least 3 characters long" : null),
		},
	});

	const prevDirty = useRef(form.isDirty());
	useEffect(() => {
		if (onChanged && prevDirty.current !== form.isDirty() && canEditVendor) {
			onChanged(form.isDirty());
			prevDirty.current = form.isDirty();
		}
	}, [form.isDirty(), onChanged]);

	useEffect(() => {
		form.setValues({
			Name: vendor?.Name || "",
			Account: vendor?.Account || "",
			Colour: vendor?.Colour || "#4dabf7",
			Description: vendor?.Description || "",
			Address: vendor?.Address || "",
			Phone: vendor?.Phone || "",
			Website: vendor?.Website || "",
			VendorType: vendor?.VendorType || "",
			Contacts: vendor?.Contacts?.length ? vendor.Contacts : [],
		});
		// eslint-disable-next-line
	}, [vendor]);

	const handleSave = (values: typeof form.values) => {
		if (!form.isValid()) {
			form.validate();
			return;
		}
		onSave(values);
	};

	const updateContact = (index: number, field: keyof VendorContactDto, value: string) => {
		const updatedContacts = form.values.Contacts.map((contact, contactIndex) =>
			contactIndex === index ? { ...contact, [field]: value } : contact
		);
		form.setFieldValue("Contacts", updatedContacts);
	};

	const addContact = () => {
		form.setFieldValue("Contacts", [...form.values.Contacts, { ...emptyContact }]);
	};

	const removeContact = (index: number) => {
		form.setFieldValue("Contacts", form.values.Contacts.filter((_, contactIndex) => contactIndex !== index));
	};

	if (vendor?.Deleted != null) {
		return (
			<Paper withBorder p="md" radius="md" style={{ height: isMobile ? 'calc(100vh)' : 'calc(100vh - 158px)' }}>
				<Stack gap="xs" mt="xl" justify="center" align="center">
					<ThemeIcon size={80} radius={80} color="red" variant="light">
						<IconTrash size={40} />
					</ThemeIcon>
					<Text fz="xl" fw="bold">
						Vendor Deleted
					</Text>
					<Text ta="center">This vendor has been deleted and cannot be edited.</Text>
				</Stack>
			</Paper>
		);
	}

	const noAccess = (
		<Paper withBorder p="md" radius="md" style={{ height: isMobile ? "calc(100vh)" : "calc(100vh - 158px)" }}>
			<Stack gap="xs" mt="xl" justify="center" align="center">
				<ThemeIcon size={80} radius={80} color="gray" variant="light">
					<IconLock size={40} />
				</ThemeIcon>
				<Text fz="xl" fw="bold">
					No Access
				</Text>
				<Text ta="center">You do not have permission to create or edit vendors. Please contact your administrator.</Text>
			</Stack>
		</Paper>
	);

	return <RoleTemplate role={AzureADRoles.Admin} noAccess={noAccess}>
		<Paper withBorder={!isMobile} p={0} radius={isMobile ? "0" : "md"} style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? "calc(100vh)" : "calc(100vh - 160px)" }}>
				<Stack gap={0} mb="md" justify="center" className={classes["header"]}>
					<Group justify="space-between" px="20" wrap="nowrap">
						<Group gap="xs" wrap="nowrap">
							<Button variant="subtle" radius="xl" ml={-15} onClick={onClose} miw={100} leftSection={<IconChevronLeft size={20} />}>
								Back
							</Button>
							<Title order={isMobile ? 6 : 3}>{isNew ? "New Vendor" : "Edit Vendor"}</Title>
						</Group>
						<Button
							onClick={() => handleSave(form.values)}
							disabled={!form.isDirty()}
							loading={isSaving}
							variant="filled"
							radius="xl"
							miw={100}
							leftSection={<IconCheck />}
						>
							Save
						</Button>
					</Group>
				</Stack>
				<Container px="20" mb="lg">
					<form onSubmit={(e) => { e.preventDefault(); }}>
						<Button type="submit" style={{ display: "none" }} />
						<TextInput
							label="Vendor Name"
							key={form.key("Name")}
							{...form.getInputProps("Name")}
							placeholder="Enter vendor name"
							maxLength={255}
							required
							mb="xl"
						/>

						<TextInput
							label="Account #"
							key={form.key("Account")}
							{...form.getInputProps("Account")}
							placeholder="Enter account"
							maxLength={255}
							mb="xl"
						/>

						<SimpleGrid cols={isMobile ? 1 : 2}>
							<Autocomplete
								label="Vendor Type"
								key={form.key("VendorType")}
								{...form.getInputProps("VendorType")}
								placeholder="Select or enter vendor type"
								data={vendorTypes ?? []}
								maxLength={255}
								mb="xl"
							/>

						<ColorInput
							key={form.key("Colour")}
							{...form.getInputProps("Colour")}
							mb="xl"
							placeholder="Pick color"
							label="Colour"
							disallowInput
							withPicker={false}
							swatchesPerRow={10}
							swatches={[
								...DEFAULT_THEME.colors.red,
								...DEFAULT_THEME.colors.pink,
								...DEFAULT_THEME.colors.grape,
								...DEFAULT_THEME.colors.violet,
								...DEFAULT_THEME.colors.indigo,
								...DEFAULT_THEME.colors.blue,
								...DEFAULT_THEME.colors.cyan,
								...DEFAULT_THEME.colors.green,
								...DEFAULT_THEME.colors.teal,
								...DEFAULT_THEME.colors.lime,
								...DEFAULT_THEME.colors.yellow,
								...DEFAULT_THEME.colors.orange
							]}
						/>

						</SimpleGrid>

						<Textarea
							label="Description"
							key={form.key("Description")}
							{...form.getInputProps("Description")}
							placeholder="Enter vendor description"
							mb="xl"
							autosize
							minRows={3}
						/>

						<TextInput
							label="Address"
							key={form.key("Address")}
							{...form.getInputProps("Address")}
							placeholder="Enter vendor address"
							mb="xl"
						/>

						<SimpleGrid cols={isMobile ? 1 : 2}>
							<TextInput
								label="Phone"
								key={form.key("Phone")}
								{...form.getInputProps("Phone")}
								placeholder="Enter phone"
								maxLength={50}
								mb="xl"
							/>
							<TextInput
								label="Website"
								key={form.key("Website")}
								{...form.getInputProps("Website")}
								placeholder="https://example.com"
								maxLength={255}
								mb="xl"
							/>
						</SimpleGrid>

						<Divider my="md" label="Contacts" labelPosition="left" />
						<Stack gap="md">
							{form.values.Contacts.map((contact, index) => (
								<Paper withBorder p="md" radius="md" key={`contact-${index}`}>
									<Group justify="space-between" align="center">
										<Text fw="600">Contact {index + 1}</Text>
										<ActionIcon variant="light" color="red" onClick={() => removeContact(index)}>
											<IconTrash size={16} />
										</ActionIcon>
									</Group>
									<Space h="sm" />
									<SimpleGrid cols={isMobile ? 1 : 2} mt="xs">
									<TextInput
										label="Full name"
										value={contact.FullName}
										onChange={(event) => updateContact(index, "FullName", event.currentTarget.value)}
										placeholder="Enter full name"
										required
									/>
									<TextInput
										label="Role"
										value={contact.Role || ""}
										onChange={(event) => updateContact(index, "Role", event.currentTarget.value)}
										placeholder="Enter role"
									/>
									<TextInput
										label="Email"
										value={contact.Email || ""}
										onChange={(event) => updateContact(index, "Email", event.currentTarget.value)}
										placeholder="name@company.com"
									/>
									<TextInput
										label="Phone number"
										value={contact.PhoneNumber || ""}
										onChange={(event) => updateContact(index, "PhoneNumber", event.currentTarget.value)}
										placeholder="Enter phone number"
									/>
									<ColorInput
										value={contact.Colour || "#4dabf7"}
										onChange={(value) => updateContact(index, "Colour", value)}
										placeholder="Pick color"
										label="Colour"
										disallowInput
										withPicker={false}
										swatchesPerRow={10}
										swatches={[
											...DEFAULT_THEME.colors.red,
											...DEFAULT_THEME.colors.pink,
											...DEFAULT_THEME.colors.grape,
											...DEFAULT_THEME.colors.violet,
											...DEFAULT_THEME.colors.indigo,
											...DEFAULT_THEME.colors.blue,
											...DEFAULT_THEME.colors.cyan,
											...DEFAULT_THEME.colors.green,
											...DEFAULT_THEME.colors.teal,
											...DEFAULT_THEME.colors.lime,
											...DEFAULT_THEME.colors.yellow,
											...DEFAULT_THEME.colors.orange
										]}
									/>
									</SimpleGrid>
								</Paper>
							))}
							<Button variant="light" leftSection={<IconPlus size={16} />} onClick={addContact}>
								Add Contact
							</Button>
						</Stack>
					</form>
				</Container>
			</ScrollArea>
		</Paper>
	</RoleTemplate>;
};

export default VendorEditView;
